import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import type { PendingQuiz, PendingQuizCounts } from '@/types';
import styles from './NotificationDropdown.module.scss';

interface NotificationDropdownProps {
  pendingQuizzes: PendingQuiz[];
  counts: PendingQuizCounts;
  onClose: () => void;
}

export function NotificationDropdown({ pendingQuizzes, counts, onClose }: NotificationDropdownProps) {
  // Show only the first 5 items in the dropdown
  const displayedQuizzes = pendingQuizzes.slice(0, 5);

  return (
    <motion.div
      className={styles.dropdown}
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
    >
      <div className={styles.dropdown__header}>
        <h3 className={styles.dropdown__title}>Pending Quizzes</h3>
        {counts.total > 0 && (
          <span className={styles.dropdown__count}>
            {counts.ready} ready
          </span>
        )}
      </div>

      <div className={styles.dropdown__content}>
        {pendingQuizzes.length === 0 ? (
          <div className={styles.dropdown__empty}>
            <p>No pending quizzes</p>
            <span>Generate a quiz to see it here</span>
          </div>
        ) : (
          <ul className={styles.dropdown__list}>
            {displayedQuizzes.map((quiz) => (
              <NotificationItem key={quiz.id} quiz={quiz} onClose={onClose} />
            ))}
          </ul>
        )}
      </div>

      {pendingQuizzes.length > 0 && (
        <div className={styles.dropdown__footer}>
          <Link
            to="/review-center"
            className={styles.dropdown__viewAll}
            onClick={onClose}
          >
            View All in Review Center
          </Link>
        </div>
      )}
    </motion.div>
  );
}

interface NotificationItemProps {
  quiz: PendingQuiz;
  onClose: () => void;
}

function NotificationItem({ quiz, onClose }: NotificationItemProps) {
  const displayName = quiz.quiz_name || truncateText(quiz.prompt, 40);
  const timeAgo = getRelativeTime(new Date(quiz.created_at));

  return (
    <li className={`${styles.dropdown__item} ${styles[`dropdown__item--${quiz.status}`]}`}>
      <Link
        to="/review-center"
        className={styles.dropdown__itemContent}
        onClick={onClose}
      >
        <div className={styles.dropdown__itemIcon}>
          <StatusIcon status={quiz.status} />
        </div>
        <div className={styles.dropdown__itemDetails}>
          <span className={styles.dropdown__itemName}>{displayName}</span>
          <span className={styles.dropdown__itemMeta}>
            <StatusLabel status={quiz.status} />
            <span className={styles.dropdown__itemTime}>{timeAgo}</span>
          </span>
        </div>
      </Link>
    </li>
  );
}

function StatusIcon({ status }: { status: PendingQuiz['status'] }) {
  if (status === 'processing') {
    return (
      <svg className={styles.dropdown__spinner} width="16" height="16" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="30" strokeLinecap="round" />
      </svg>
    );
  }
  if (status === 'ready') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // failed
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 6L10 10M10 6L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StatusLabel({ status }: { status: PendingQuiz['status'] }) {
  const labels = {
    processing: 'Generating...',
    ready: 'Ready for review',
    failed: 'Failed',
  };

  return (
    <span className={`${styles.dropdown__statusLabel} ${styles[`dropdown__statusLabel--${status}`]}`}>
      {labels[status]}
    </span>
  );
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}
